import { obtenerVentas } from './ventasService';
import { obtenerGastos } from './gastosService';
import { obtenerCuentas } from './cuentasService';
import { obtenerProductos } from './productosService';
import { fechaLocal, fechaLocalHaceDias } from '../utils/fecha';
import { getUmbralStock } from '../utils/stockBajo';
import { obtenerVentasDelDia } from './ventasDiaService';
import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';

const inicioDeHoy = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Reúne en una sola llamada los indicadores del dashboard (cap. 11).
// soloDe: id de usuario → "Ventas de hoy" cuenta solo lo que esa persona vendió.
export async function obtenerResumenDashboard({ soloDe = null } = {}) {
  // Ventas libres de los últimos 7 días (para la gráfica por día).
  let qLibres = supabase.from('ingresos_libres').select('monto, costo, fecha, creado_en, usuario_id, ticket_venta, concepto, envio')
    .eq('negocio_id', getNegocioId()).gte('fecha', fechaLocalHaceDias(6));
  if (soloDe) qLibres = qLibres.eq('usuario_id', soloDe);

  const [ventas7, gastos7, cuentas, productos, delDia, libres7] = await Promise.all([
    obtenerVentas({ desde: new Date(Date.now() - 7 * 86400000).toISOString() }),
    obtenerGastos({ desde: fechaLocalHaceDias(7) }),
    obtenerCuentas(),
    obtenerProductos(),
    obtenerVentasDelDia(fechaLocal(), soloDe),
    qLibres.then((r) => { if (r.error) throw r.error; return r.data || []; }),
  ]);

  const hoy = inicioDeHoy();
  const esHoy = (iso) => new Date(iso) >= hoy;
  const fechaHoy = fechaLocal();

  // Ventas de hoy: productos + ventas libres (de quien corresponda)
  const ventasHoy = ventas7.filter((v) => esHoy(v.vendido_en) && (!soloDe || v.vendido_por === soloDe));
  // Los envíos cobrados no son venta ni ganancia (entran y se le pagan al courier).
  const esEnvioLibre = (i) => !!i.ticket_venta || i.concepto === 'Envío nacional' || Number(i.envio || 0) > 0;
  const delDiaVenta = delDia.filter((x) => !x.envio);
  const totalHoy = delDiaVenta.reduce((s, x) => s + x.monto, 0);
  const enviosHoyMonto = delDia.filter((x) => x.envio).reduce((s, x) => s + x.monto, 0);
  const unidadesHoy = ventasHoy.reduce((s, v) => s + (v.cantidad || 0), 0);
  const ventasLibresHoy = delDia.filter((x) => x.tipo === 'libre' && !x.envio).length;
  const enviosHoy = delDia.filter((x) => x.tipo === 'libre' && x.envio).length;
  const gananciaHoy = delDiaVenta.reduce((s, x) => s + x.monto - x.costo, 0);

  // Últimos 7 días → utilidad real (obtenerGastos ya filtró por fecha)
  const ventas7Total = ventas7.reduce((s, v) => s + Number(v.total || 0), 0);
  const costo7 = ventas7.reduce((s, v) => s + (v.cantidad || 0) * Number(v.costo_unitario || 0), 0);
  const gastos7Total = gastos7.reduce((s, g) => s + Number(g.monto || 0), 0);
  const gastosHoy = gastos7.filter((g) => g.fecha === fechaHoy).reduce((s, g) => s + Number(g.monto || 0), 0);
  const utilidad7 = ventas7Total - costo7 - gastos7Total;

  // Dinero en cuentas
  const disponible = cuentas.filter((c) => c.tipo !== 'tarjeta_credito').reduce((s, c) => s + Number(c.saldo || 0), 0);
  const deuda = cuentas.filter((c) => c.tipo === 'tarjeta_credito' && Number(c.saldo) < 0).reduce((s, c) => s + Number(c.saldo || 0), 0);

  // Inventario
  const stockBajo = productos.filter((p) => (p.stock || 0) <= getUmbralStock());
  const valorInventarioCosto = productos.reduce((s, p) => s + (p.stock || 0) * Number(p.precio_costo || 0), 0);

  // --- Gráficas de "cómo va hoy" ---
  // Ventas por día: los últimos 7 días calendario (hoy incluido).
  const dias = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(hoy);
    d.setDate(d.getDate() - i);
    dias.push({ fecha: fechaLocal(d), dia: d, ventas: 0, costo: 0, gastos: 0, esHoy: i === 0 });
  }
  const porFecha = Object.fromEntries(dias.map((d) => [d.fecha, d]));
  const ventasSerie = ventas7.filter((v) => !soloDe || v.vendido_por === soloDe);
  ventasSerie.forEach((v) => {
    const d = porFecha[fechaLocal(new Date(v.vendido_en))];
    if (!d) return;
    d.ventas += Number(v.total || 0);
    d.costo += (v.cantidad || 0) * Number(v.costo_unitario || 0);
  });
  const libresVenta7 = libres7.filter((i) => !esEnvioLibre(i));
  libresVenta7.forEach((i) => {
    const d = porFecha[i.fecha];
    if (!d) return;
    d.ventas += Number(i.monto || 0);
    d.costo += Number(i.costo || 0);
  });
  gastos7.forEach((g) => { const d = porFecha[g.fecha]; if (d) d.gastos += Number(g.monto || 0); });
  // El día de hoy sale de la misma fuente que la tarjeta "Ventas de hoy".
  const diaHoy = dias[dias.length - 1];
  diaHoy.ventas = totalHoy;
  diaHoy.costo = totalHoy - gananciaHoy;

  // Promedio diario de los 6 días anteriores (para comparar con hoy).
  const promedioDiario = dias.slice(0, -1).reduce((s, d) => s + d.ventas, 0) / 6;

  // Ventas de hoy por hora.
  const porHora = Array.from({ length: 24 }, (_, h) => ({ hora: h, monto: 0, ventas: 0 }));
  const vistos = new Set();
  delDiaVenta.forEach((x) => {
    const h = new Date(x.hora).getHours();
    porHora[h].monto += x.monto;
    const t = x.ticket || x.ticketVenta; // un ticket = una venta (su envío va dentro)
    const clave = t ? `t-${t}` : x.id;
    if (!vistos.has(clave)) { vistos.add(clave); porHora[h].ventas += 1; }
  });
  const numVentasHoy = vistos.size;

  // Hora más fuerte de la semana (para sugerir cuándo vender más).
  const semanaHora = Array(24).fill(0);
  ventasSerie.forEach((v) => {
    if (new Date(v.vendido_en) >= dias[0].dia) semanaHora[new Date(v.vendido_en).getHours()] += Number(v.total || 0);
  });
  libresVenta7.forEach((i) => { if (i.creado_en) semanaHora[new Date(i.creado_en).getHours()] += Number(i.monto || 0); });
  const maxSemana = Math.max(...semanaHora);
  const mejorHora = maxSemana > 0 ? semanaHora.indexOf(maxSemana) : null;

  return {
    dias, promedioDiario, porHora, numVentasHoy, mejorHora,
    totalHoy, unidadesHoy, ventasLibresHoy, enviosHoy, enviosHoyMonto, gananciaHoy, gastosHoy,
    ventas7Total, utilidad7,
    cuentas, disponible, deuda,
    stockBajo, totalProductos: productos.length, valorInventarioCosto,
  };
}
